import React from 'react'
import styled, { css } from 'styled-components'
import { Dropdown } from '../Dropdown'
import { Icon } from '../Icon'
import { createPortal } from 'react-dom'

const StyledTr = styled.tr<{
  clickable?: boolean
  selectable?: boolean
  selected?: boolean
  showMenu?: boolean
}>`
  ${({ clickable }) =>
    clickable &&
    css`
      &:focus {
        outline: none;
        cursor: pointer;
        [role="cell"] {
          background-color: var(--surface-secondary);
        }
        [role="cell"]:first-of-type {
          box-shadow: inset 4px 1px 0 -1px var(--brand-primary);
        }
      }
    `}

  ${({ selectable }) =>
    selectable &&
    css`
      &:focus {
        outline: none;
        cursor: pointer;
      }

      &:hover {
        [role="cell"] {
          background-color: var(--surface-secondary);
        }
        [role="cell"]:first-of-type {
          box-shadow: inset 4px 1px 0 -1px var(--brand-primary);
        }
      }
    `}

    ${({ selected }) =>
      selected &&
      css`
      [role="cell"] {
        background-color: var(--surface-secondary);
      }

      &:hover {
        [role="cell"] {
          border-color: var(--border);
        }
      }

      [role="cell"]:first-of-type {
        box-shadow: inset 3px 1px 0 -1px var(--brand-primary);
      }
    `}

  ${({ showMenu }) =>
    showMenu &&
    css`
      position: relative;
      
      [role="cell"]:nth-last-child(2) {
        padding-right: 24px !important;
      }

      &:hover {
        .menu-indicator {
          opacity: 1;
        }
      }
    `}
`

export type TrProps = React.ComponentPropsWithoutRef<'tr'> &
  TableContextMenuProps & {
    clickable?: boolean
    selectable?: boolean
    selected?: boolean
    showMenu?: boolean
    onContextMenu?: (e: React.MouseEvent) => void
  }

export const Tr = React.forwardRef<HTMLTableRowElement, TrProps>(
  (
    {
      onClick,
      showMenu,
      menuContent,
      menuWrapperProps,
      onContextMenu,
      menuIndicator,
      menuIndicatorProps,
      ...rest
    },
    ref
  ) => {
    const [trElem, setTrElem] = React.useState<HTMLTableRowElement | null>(null)
    const [contextMenuPos, setContextMenuPos] = React.useState<{
      x: number
      y: number
    } | null>(null)

    const doKeyboardClick = (e: KeyboardEvent) => {
      if (e.code === 'Enter') trElem?.click()
    }

    React.useEffect(() => {
      if (typeof ref === 'function' && trElem) {
        ref(trElem)
      }

      if (onClick) {
        trElem?.addEventListener('keydown', doKeyboardClick)
      }

      return () => trElem?.removeEventListener('keydown', doKeyboardClick)
    }, [trElem, onClick])

    const handleContextMenu = (e: React.MouseEvent) => {
      if (!showMenu) return

      e.preventDefault()
      if (onContextMenu) {
        onContextMenu(e)
      }

      const rect = trElem?.getBoundingClientRect()
      if (rect) {
        setContextMenuPos({
          x: e.clientX + window.scrollX,
          y: e.clientY + window.scrollY,
        })
      }
    }

    React.useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          contextMenuPos &&
          !(e.target as Element)?.closest('.dispense-dropdown-menu') &&
          !(e.target as Element)?.closest('.dispense-dropdown-item')
        ) {
          setContextMenuPos(null)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [contextMenuPos])

    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setContextMenuPos(null)
        }
      }

      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])

    return (
      <>
        <StyledTr
          clickable={Boolean(onClick)}
          tabIndex={onClick ? 0 : undefined}
          ref={setTrElem}
          role='row'
          onMouseEnter={() => trElem?.focus()}
          data-testid='table-row'
          showMenu={showMenu}
          onClick={(e) => {
            // @ts-expect-error
            if (e.target.closest('.menu-indicator')) {
              e.stopPropagation()
              return
            }

            if (onClick) {
              onClick(e)
            }
          }}
          onContextMenu={handleContextMenu}
          {...rest}
        >
          {rest.children}
          {showMenu && (
            <TableContextMenu
              menuContent={menuContent}
              menuWrapperProps={menuWrapperProps}
              menuIndicator={menuIndicator}
              menuIndicatorProps={menuIndicatorProps}
            />
          )}
        </StyledTr>

        {contextMenuPos &&
          createPortal(
            <Dropdown
              isOpen={true}
              onClose={() => setContextMenuPos(null)}
              Trigger={({ toggleDropdown }) => <div />}
              position='left'
              css={css`
              position: fixed;
              left: ${Math.max(contextMenuPos.x - 24, 0)}px;
              top: ${Math.min(contextMenuPos.y + 12, window.innerHeight - 12)}px;
              z-index: 1000;
            `}
            >
              {menuContent?.(() => setContextMenuPos(null))}
            </Dropdown>,
            document.body
          )}
      </>
    )
  }
)

type TableContextMenuProps = {
  menuContent?: (close: () => void) => React.ReactNode
  menuWrapperProps?: React.HTMLAttributes<HTMLDivElement>
  menuIndicatorProps?: React.HTMLAttributes<HTMLDivElement>
  menuIndicator?: React.ReactNode
}

const MenuIndicator = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  position: relative;

  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1;
  cursor: pointer;

  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 24px;
    color: var(--text-secondary);
  }
`

const TableContextMenu = ({
  menuContent,
  menuWrapperProps,
  menuIndicatorProps,
  menuIndicator,
}: TableContextMenuProps) => {
  return (
    <td
      data-testid='table-context-menu'
      css={`
        position: relative;
        padding: 0 !important;
        width: 1px;
      `}
    >
      <div
        {...menuWrapperProps}
        css={`
          position: absolute;
          right: 16px;
          top: 0;
          height: 100%;
          display: flex;
          align-items: center;
        `}
      >
        <Dropdown
          usePortal
          Trigger={({ toggleDropdown }) => (
            <MenuIndicator
              className='menu-indicator'
              onClick={toggleDropdown}
              {...menuIndicatorProps}
            >
              {menuIndicator ?? <Icon type='MORE' height={24} width={24} />}
            </MenuIndicator>
          )}
          position='right'
        >
          {({ close }) => menuContent?.(close)}
        </Dropdown>
      </div>
    </td>
  )
}
